import { BsInfoCircle } from "react-icons/bs";
import useOverviewAnalytics from "./useOverviewAnalytics";
import Skeleton from "react-loading-skeleton";
import StatBox from "../../../../components/StatBox/StatBox";
import "react-tooltip/dist/react-tooltip.css";
import { useEffect, useState } from "react";
import "../Analytics.css";
import { AgChartsReact } from "ag-charts-react";
import { BannerSkeleton } from "../../../../components/StayUpToDateComponent/BannerSkeleton";

// Function to generate time points for a 24-hour chart
const generate24HourTimePoints = () => {
  const result = [];
  const today = new Date();
  
  // Generate points for every 6 hours (00:00, 06:00, 12:00, 18:00)
  for (let hour = 0; hour < 24; hour += 6) {
    const date = new Date(today);
    date.setHours(hour, 0, 0, 0);
    
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit'
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).substring(0, 5); // Get only HH:MM part
    
    result.push({
      formatted_date: formattedDate,
      hour: hour, // Store the hour value explicitly
      users: 0,
      searches: 0,
      links: 0,
      page_views: 0,
      display_time: formatTimeFor24HourView(hour)
    });
  }
  
  // Add one more point for 00:00 next day to complete the 24 hours
  const nextDay = new Date(today);
  nextDay.setDate(nextDay.getDate() + 1);
  nextDay.setHours(0, 0, 0, 0);
  
  const formattedDate = nextDay.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit'
  }) + ' ' + '00:00';
  
  result.push({
    formatted_date: formattedDate,
    hour: 0, // Store the hour value explicitly
    users: 0,
    searches: 0,
    links: 0,
    page_views: 0,
    display_time: formatTimeFor24HourView(0)
  });
  
  return result;
};

// Format time for 24-hour view
const formatTimeFor24HourView = (hour: number) => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

// Process data specifically for 24-hour chart
const process24HourData = (data: any[]) => {
  if (!data || data.length === 0) {
    // If no data, return default 24-hour time points
    return generate24HourTimePoints();
  }

  // Filter out interpolated points (those with -1, -2, -3 suffixes)
  const mainPoints = data.filter(point => !point.formatted_date.includes('-'));
  
  // Add display_time field for better tooltip and axis display
  return mainPoints.map(point => {
    // Extract hour from the formatted_date (expected format: "Apr 25 06:00")
    let hourStr = '0';
    let hour = 0;
    try {
      const timePart = point.formatted_date.split(' ')[2]; // Get "06:00"
      hourStr = timePart.split(':')[0]; // Get "06"
      hour = parseInt(hourStr, 10);
    } catch (e) {
      console.error('Error parsing time from', point.formatted_date, e);
    }
    
    return {
      ...point,
      hour: hour, // Add explicit hour field
      display_time: formatTimeFor24HourView(hour)
    };
  });
};

// Separate formatting function for tooltip
const formatTooltipFor24Hour = (params: any) => {
  // Use the display_time if available, otherwise format from the date
  const displayTime = params.datum.display_time || 
    (new Date(params.datum.formatted_date)).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  
  const value = Math.round(params.datum[params.yKey]);
  
  return {
    content: `
      <div style="padding: 5px;">
        <div class="tooltip-value">${value}</div>
      </div>
    `
  };
};

const Overview24hoursCharts = ({
  user_id,
  startDate,
  endDate,
  customCategories,
  instagramLinks,
  showSocials,
  brand_color,
  isYesterday,
  daysNumber,
}: any) => {
  instagramLinks = instagramLinks && instagramLinks.split(",");

  // Format dates to ensure they're strings
  const formattedStartDate =
    typeof startDate === "object" ? startDate.toString() : startDate;
  const formattedEndDate =
    typeof endDate === "object" ? endDate.toString() : endDate;

  const {
    loadingUniqueUsers,
    uniqueUsers,
    loadingPageViews,
    pageViews,
    loadingHashtagSearches,
    hashtagSearches,
    loadingSearchBarSearches,
    searchBarSearches,
    loadingPopularSearches,
    popularSearches,
    instagramHighlightsSearches,
    loadinginstagramHighlightsSearches,
    customCategoriesSearches,
    loadingCustomCategoriesSearches,
    loadinginstagramLinksSearches,
    instagramLinksSearches,
    loadingTotalSearchCount,
    totalSearchCount,
    totalShoppableClicks,
    loadingTotalShoppableClicks,
    pageAnalytics,
    loadingPageAnalytics,
  } = useOverviewAnalytics(
    user_id,
    startDate,
    endDate,
    customCategories,
    instagramLinks,
    isYesterday,
    daysNumber
  );

  // Ensure pageAnalytics data has formatted dates
  const formattedPageAnalytics =
    pageAnalytics?.map((item: any) => ({
      ...item,
      formatted_date:
        typeof item.formatted_date === "object"
          ? item.formatted_date.toString()
          : item.formatted_date,
    })) || [];

  // Process data specifically for 24-hour view
  const hourlyData = process24HourData(formattedPageAnalytics);
  
  // AG Charts options for 24-hour view
  const [chartOptions, setChartOptions] = useState<any>({
    autoSize: true,
    title: {
      text: "",
      fontSize: 18,
    },
    data: hourlyData,
    series: [
      {
        type: "line",
        xKey: "formatted_date",
        yKey: "users",
        yName: "Unique Users",
        stroke: "#4F46E5", // Blue color
        strokeWidth: 3,
        marker: {
          enabled: true, // Enable markers for hourly points
          size: 6,
          fill: 'white',
          stroke: '#4F46E5', // Blue color matching the line
          strokeWidth: 2,
        },
        tooltip: {
          enabled: true,
          renderer: (params: any) => formatTooltipFor24Hour(params),
        },
        gridStyle: [{ stroke: "#E5E7EB", lineDash: [5, 5] }],
        smoothing: 0.3, // Lower smoothing for more accurate 24h representation
        lineDash: [0],
        interpolation: "monotoneX",
      },
      {
        type: "line",
        xKey: "formatted_date",
        yKey: "searches",
        yName: "Searches",
        stroke: "#10B981", // Green color
        strokeWidth: 3,
        marker: {
          enabled: true, // Enable markers for hourly points
          size: 6,
          fill: 'white',
          stroke: '#10B981', // Green color matching the line
          strokeWidth: 2,
        },
        tooltip: {
          enabled: true,
          renderer: (params: any) => formatTooltipFor24Hour(params),
        },
        gridStyle: [{ stroke: "#E5E7EB", lineDash: [5, 5] }],
        smoothing: 0.3, // Lower smoothing for more accurate 24h representation
        lineDash: [0],
        interpolation: "monotoneX",
      },
      {
        type: "line",
        xKey: "formatted_date",
        yKey: "links",
        yName: "Shoppable Clicks",
        stroke: "#8B5CF6", // Purple color
        marker: {
          enabled: true,
          size: 6,
          fill: 'white',
          stroke: '#8B5CF6', // Purple color matching the line
          strokeWidth: 2,
        },
        tooltip: {
          enabled: true,
          renderer: (params: any) => formatTooltipFor24Hour(params),
        },
        gridStyle: [{ stroke: "#E5E7EB", lineDash: [5, 5] }],
        smoothing: 0.3,
        visible: false,
      },
      {
        type: "line",
        xKey: "formatted_date",
        yKey: "page_views",
        yName: "Page Views",
        stroke: "#F59E0B", // Amber color
        marker: {
          enabled: true,
          size: 6,
          fill: 'white',
          stroke: '#F59E0B', // Amber color matching the line
          strokeWidth: 2,
        },
        tooltip: {
          enabled: true,
          renderer: (params: any) => formatTooltipFor24Hour(params),
        },
        gridStyle: [{ stroke: "#E5E7EB", lineDash: [5, 5] }],
        smoothing: 0.3,
        visible: false,
      },
    ],
    axes: [
      {
        type: "category",
        position: "bottom",
        title: { enabled: false },
        label: {
          fontSize: 12,
          color: "#6B7280",
          rotation: 0,
          // formatter: (params: any) => {
          //   // Display only specific fixed labels based on the hour
          //   const timeLabel = params.value?.split(' ')?.[2]; // Get "06:00" part
          //   if (!timeLabel) return '';
            
          //   const hour = parseInt(timeLabel.split(':')[0], 10);
            
          //   // Only show these specific hours
          //   if (hour === 0 || hour === 24) return '12am';
          //   if (hour === 6) return '6am';
          //   if (hour === 12) return '12pm';
          //   if (hour === 18) return '6pm';
            
          //   return ''; // Hide other labels
          // }
          formatter: (params: any) => {
            const parts = params.value?.split(' ');
            if (!parts || parts.length < 3) return '';
          
            const date = `${parts[0]} ${parts[1]}`; // "Apr 25"
            const time = parts[2]; // "06:00"
          
            const hour = parseInt(time.split(':')[0], 10);
          
            // Only show specific hours
            let timeLabel = '';
            if (hour === 0 || hour === 24) timeLabel = '12am';
            else if (hour === 6) timeLabel = '6am';
            else if (hour === 12) timeLabel = '12pm';
            else if (hour === 18) timeLabel = '6pm';
          
            return timeLabel ? `${date} ${timeLabel}` : '';
          }
          
          
        },
        gridStyle: [{ stroke: "#E5E7EB", lineDash: [5, 5] }],
      },
      {
        type: "number",
        position: "left",
        title: { enabled: false },
        label: {
          fontSize: 12,
          color: "#6B7280",
        },
        gridStyle: [{ stroke: "#E5E7EB", lineDash: [5, 5] }],
      },
    ],
    background: {
      fill: "white",
    },
    padding: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20,
    },
    legend: {
      position: "bottom",
      spacing: 40,
      item: {
        marker: {
          shape: "circle",
          size: 8,
        },
        label: {
          fontSize: 14,
          color: "#4B5563",
        },
      },
    },
    tooltipTracking: true,
    tooltipClass: "ag-chart-tooltip",
  });
  // Update chart options when data changes
  useEffect(() => {
    // Always use the hourlyData that has our key time points added
    setChartOptions((prevOptions: any) => ({
      ...prevOptions,
      data: hourlyData, // Use the hourlyData with our added key points
      series: [
        {
          ...prevOptions.series[0],
          stroke: "#4F46E5", // Blue color
          strokeWidth: 3,
          marker: {
            enabled: true,
            size: 6,
            fill: 'white',
            stroke: '#4F46E5', // Blue color matching the line
            strokeWidth: 2,
          },
          smoothing: 0.6, // Increase smoothing for more curved lines
          interpolation: "monotoneX", // Use monotone interpolation for smoother curves
        },
        {
          ...prevOptions.series[1],
          stroke: "#10B981", // Green color
          strokeWidth: 3,
          marker: {
            enabled: true,
            size: 6,
            fill: 'white',
            stroke: '#10B981', // Green color matching the line
            strokeWidth: 2,
          },
          smoothing: 0.6, // Increase smoothing for more curved lines
          interpolation: "monotoneX", // Use monotone interpolation for smoother curves
        },
        {
          ...prevOptions.series[2],
          visible: true, // Hide by default to match the image with just two lines
        },
        {
          ...prevOptions.series[3],
          visible: true, // Hide by default to match the image with just two lines
        },
      ],
    }));
  }, [formattedPageAnalytics?.length]); // Only depend on the length of hourlyData



  return (
    <>
      <div className="loadingPageAnalytics">
        <div className="fulliv">
          {loadingPageAnalytics ? (
            <>
              <BannerSkeleton />
              <div className="skeleton-analytics-titles">
                {Array(4).fill(<SkeletonStatBox />)}
              </div>
            </>
          ) : (
            <div className="analytics">
              {/* TailGrids Styled Chart Container */}
              <div className="w-full bg-white rounded-lg shadow-md p-6 border border-gray-100 smooth-chart-container">
                <div
                  className="bg-white rounded-lg p-2 chart-wrapper-mobile"
                  style={{ 
                    height: "400px", 
                    position: "relative",
                    touchAction: "pan-y", // Allow vertical scrolling
                    overscrollBehavior: "contain", // Prevent overscroll issues
                    WebkitOverflowScrolling: "touch" // Enable momentum scrolling on iOS
                  }}
                >
                  <AgChartsReact
                    options={{
                      ...chartOptions,
                      // Force specific options that ensure smooth curves
                      series: chartOptions.series.map((series: any) => ({
                        ...series,
                        smoothing: 0.8, // Increase smoothing even more
                        interpolation: "cardinal", // Try cardinal interpolation for smoother curves
                        strokeWidth: 3,
                        marker: { enabled: false },
                        lineDash: [0],
                        strokeOpacity: 1,
                      })),
                      // Axes configuration comes from chartOptions
                      theme: {
                        palette: {
                          fills: ["#4F46E5", "#10B981", "#8B5CF6", "#F59E0B"],
                          strokes: ["#4F46E5", "#10B981", "#8B5CF6", "#F59E0B"],
                        },
                        overrides: {
                          line: {
                            series: {
                              strokeWidth: 3,
                              marker: {
                                enabled: false,
                              },
                              tooltip: {
                                enabled: true,
                              },
                            },
                          },
                        },
                      },
                      // Additional options for smoother rendering
                      animation: {
                        enabled: true,
                      },
                      // Disable chart interactions that interfere with scrolling
                      interactions: {
                        enabled: false,
                      },
                      // Disable zoom and pan to prevent touch event conflicts
                      zoom: {
                        enabled: false,
                      },
                      // Ensure touch events don't interfere with scrolling
                      navigator: {
                        enabled: false,
                      },
                      // Mobile-specific options to prevent touch conflicts
                      mobile: {
                        enabled: true,
                      },
                      // Disable any drag interactions
                      drag: {
                        enabled: false,
                      },
                    }}
                  />
                </div>
              </div>

              {/* Stats Cards */}
              <div className="analytics-titles" id="page-charts">
                <StatBox
                  isLoading={loadingPageViews}
                  name="Page Views"
                  number={pageViews?.total}
                  helperText="Number of times your page has been visited."
                  classname="stat-page"
                  brand_color={brand_color}
                  breakdownData={pageViews?.sources}
                  showBreakdown={true}
                />
                <StatBox
                  isLoading={loadingUniqueUsers}
                  name="Unique Users"
                  number={uniqueUsers?.total}
                  helperText="Number of unique visitors who have viewed your page."
                  classname="stat-page"
                  brand_color={brand_color}
                />
                <StatBox
                  isLoading={loadingTotalSearchCount}
                  name="Total Searches"
                  number={totalSearchCount}
                  helperText="Total number of searches performed on your page."
                  classname="stat-page"
                  brand_color={brand_color}
                />
                <StatBox
                  isLoading={loadingTotalShoppableClicks}
                  name="Shoppable Clicks"
                  number={totalShoppableClicks}
                  helperText="Number of times visitors clicked on shoppable links."
                  classname="stat-page"
                  brand_color={brand_color}
                />
              </div>
            </div>
          )}
        </div>
      </div>

     
    </>
  );
};

export default Overview24hoursCharts;


export function SkeletonStatBox() {
  return (
    <div className="skeleton-analytics-grid-item">
      <Skeleton style={{ height: "50px" }} />
    </div>
  );
}
